import { proxyCustomElement, HTMLElement, createEvent, h } from '@stencil/core/internal/client';
import { c as create$3, d as create$7, F as FormController } from './form.js';
import { B as BusinessFormStep } from './business-form-types.js';
import { c as config } from './config.js';
import { A as Api } from './Api.js';
import './Pagination.js';
import './Payment.js';
import './Payout.js';
import { C as ComponentErrorCodes, a as ComponentErrorSeverity } from './ComponentError.js';
import { d as defineCustomElement$4 } from './form-control-checkbox2.js';
import { d as defineCustomElement$3 } from './form-control-error-text2.js';
import { d as defineCustomElement$2 } from './form-control-help-text2.js';
import { d as defineCustomElement$1 } from './business-terms-conditions-text.js';

const businessTermsConditionsSchema = (allowOptionalFields) => {
    const schema = create$3({
        accepted: create$7()
            .oneOf([true], 'You must agree to the terms and conditions to continue')
            .required('You must agree to the terms and conditions to continue')
    });
    const easySchema = create$3({
        accepted: create$7().nullable()
    });
    return allowOptionalFields ? easySchema : schema;
};

const BusinessTermsConditionsFormStep = /*@__PURE__*/ proxyCustomElement(class BusinessTermsConditionsFormStep extends HTMLElement {
    constructor() {
        super();
        this.__registerHost();
        this.submitted = createEvent(this, "submitted", 7);
        this.formLoading = createEvent(this, "formLoading", 7);
        this.errorEvent = createEvent(this, "error-event", 7);
        this.fetchData = async () => {
            this.formLoading.emit(true);
            try {
                const response = await this.api.get(this.businessEndpoint);
                this.acceptedTermsBefore = response.data.terms_conditions_accepted;
            }
            catch (error) {
                this.errorEvent.emit({
                    errorCode: ComponentErrorCodes.FETCH_ERROR,
                    message: error.message,
                    severity: ComponentErrorSeverity.ERROR,
                    data: error,
                });
            }
            finally {
                this.formLoading.emit(false);
            }
        };
        this.sendData = async (onSuccess) => {
            this.formLoading.emit(true);
            try {
                const payload = this.termsPayload;
                const response = await this.api.post(this.termsConditionsEndpoint, payload);
                this.handleResponse(response, onSuccess);
            }
            catch (error) {
                this.errorEvent.emit({
                    errorCode: ComponentErrorCodes.POST_ERROR,
                    message: error.message,
                    severity: ComponentErrorSeverity.ERROR,
                    data: error,
                });
            }
            finally {
                this.formLoading.emit(false);
            }
        };
        this.inputHandler = (name, value) => {
            this.formController.setValues(Object.assign(Object.assign({}, this.formController.values.getValue()), { [name]: value }));
        };
        this.formController = undefined;
        this.errors = {};
        this.acceptedTermsBefore = undefined;
        this.authToken = undefined;
        this.businessId = undefined;
        this.allowOptionalFields = undefined;
    }
    get businessEndpoint() {
        return `entities/business/${this.businessId}`;
    }
    get termsConditionsEndpoint() {
        return 'entities/terms_and_conditions';
    }
    get formHelperText() {
        return this.acceptedTermsBefore ? 'You have already accepted the terms and conditions.' : null;
    }
    get termsPayload() {
        return {
            business_id: this.businessId,
            accepted: this.formController.values.getValue().accepted,
            user_agent: window.navigator.userAgent
        };
    }
    async componentWillLoad() {
        this.api = Api({ authToken: this.authToken, apiOrigin: config.proxyApiOrigin });
        this.formController = new FormController(businessTermsConditionsSchema(this.allowOptionalFields));
        if (this.businessId && this.authToken) {
            this.fetchData();
        }
    }
    handleResponse(response, onSuccess) {
        if (response.error) {
            this.errorEvent.emit({
                errorCode: ComponentErrorCodes.POST_ERROR,
                message: response.error.message,
                severity: ComponentErrorSeverity.ERROR,
                data: response.error,
            });
        }
        else {
            onSuccess();
        }
        this.submitted.emit({ data: response, metadata: { completedStep: BusinessFormStep.termsAndConditions } });
    }
    async validateAndSubmit({ onSuccess }) {
        if (this.acceptedTermsBefore) {
            this.submitted.emit({ metadata: { completedStep: BusinessFormStep.termsAndConditions } });
            onSuccess();
        }
        else {
            this.formController.validateAndSubmit(() => this.sendData(onSuccess));
        }
    }
    ;
    componentDidLoad() {
        this.formController.errors.subscribe(errors => {
            this.errors = Object.assign({}, errors);
        });
    }
    render() {
        return (h("form", { key: 'a292e2b1b69d152009000d9e7009e5ab07698bc8' }, h("fieldset", { key: '30843e9ed0029f64abcf076512af82974dd99598' }, h("legend", { key: '6453679a074056668bdd93cb91d2d725b73fb0da' }, "Terms and Conditions"), h("hr", { key: '6a44c1128e3dceb770b16533aa61fb5e81f5dda2' }), h("justifi-business-terms-conditions-text", { key: '714c9f81c1d1f91712a276259acf4eb6734b40d4' }), h("br", { key: '5e955908e53af5ade77fca9f2a47e8a9d10e7ad8' }), h("div", { key: '7c6ca9d2634c7cc6988292ffedf86b64c3865557', class: "row-gy-3" }, h("div", { key: 'e8f01c4bdcc07e0063e3b8f5f1acbabc9b3310f7', class: "col-12" }, h("form-control-checkbox", { key: 'e09eeb02dc30a9461e958bf43685d2c58f4a608f', name: "accepted", label: "I agree to the terms and conditions", inputHandler: this.inputHandler, errorText: this.errors.accepted, disabled: this.acceptedTermsBefore, helpText: this.formHelperText }))))));
    }
}, [0, "justifi-business-terms-conditions-form-step", {
        "authToken": [1, "auth-token"],
        "businessId": [1, "business-id"],
        "allowOptionalFields": [4, "allow-optional-fields"],
        "formController": [32],
        "errors": [32],
        "acceptedTermsBefore": [32],
        "validateAndSubmit": [64]
    }]);
function defineCustomElement() {
    if (typeof customElements === "undefined") {
        return;
    }
    const components = ["justifi-business-terms-conditions-form-step", "form-control-checkbox", "form-control-error-text", "form-control-help-text", "justifi-business-terms-conditions-text"];
    components.forEach(tagName => { switch (tagName) {
        case "justifi-business-terms-conditions-form-step":
            if (!customElements.get(tagName)) {
                customElements.define(tagName, BusinessTermsConditionsFormStep);
            }
            break;
        case "form-control-checkbox":
            if (!customElements.get(tagName)) {
                defineCustomElement$4();
            }
            break;
        case "form-control-error-text":
            if (!customElements.get(tagName)) {
                defineCustomElement$3();
            }
            break;
        case "form-control-help-text":
            if (!customElements.get(tagName)) {
                defineCustomElement$2();
            }
            break;
        case "justifi-business-terms-conditions-text":
            if (!customElements.get(tagName)) {
                defineCustomElement$1();
            }
            break;
    } });
}
defineCustomElement();

export { BusinessTermsConditionsFormStep as B, defineCustomElement as d };
